import React from 'react';
import { Link } from 'react-router-dom';
import { ShoppingBag, Search, ShoppingCart, Heart, Package, Info, Phone, FileText, Lock, Facebook, Instagram, Leaf, Sprout } from 'lucide-react';

interface FooterProps {
  companyName?: string;
  year?: number;
}

const Footer: React.FC<FooterProps> = ({
  companyName = "Green Guest",
  year = new Date().getFullYear()
}) => {
  return (
    <footer className="bg-gradient-to-br from-green-800 via-emerald-800 to-teal-800 text-white py-8 sm:py-12 lg:py-16 relative overflow-hidden">
      {/* Background Nature Elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-4 sm:left-10 text-green-300 transform rotate-12"><Leaf className="w-12 h-12 sm:w-16 sm:h-16" /></div>
        <div className="absolute top-20 right-4 sm:right-20 text-emerald-300 transform -rotate-12"><Sprout className="w-8 h-8 sm:w-10 sm:h-10" /></div>
        <div className="absolute bottom-20 left-4 sm:left-20 text-teal-300 transform rotate-45"><Leaf className="w-10 h-10 sm:w-12 sm:h-12" /></div>
        <div className="absolute bottom-10 right-4 sm:right-10 text-green-300 transform -rotate-30"><Sprout className="w-6 h-6 sm:w-8 sm:h-8" /></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-6 sm:mb-8">
          {/* Company Info */}
          <div className="sm:col-span-2 lg:col-span-1">
            <div className="flex items-center gap-3 mb-4 sm:mb-6">
              <img src="/logo.png" alt="GreenGuest logo" className="h-8 w-8 sm:h-10 sm:w-10 rounded-full shadow-lg object-cover" />
              <h3 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-white to-green-200 bg-clip-text text-transparent">
                {companyName}
              </h3>
            </div>
            <p className="text-green-100 text-sm leading-relaxed">
              Your trusted source for authentic bonsai trees, tools, and accessories.
              Cultivating beauty and tranquility for over 7 years.
            </p>
          </div>

          {/* Shop Links */}
          <div className="flex flex-col gap-3 sm:gap-4">
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-green-200 flex items-center gap-2">
              <ShoppingBag className="w-5 h-5" /> Shop
            </h4>
            <ul className="list-none m-0 p-0 flex flex-col gap-2 sm:gap-3">
              <li><Link to="/search" className="text-green-100 no-underline transition-all duration-300 text-sm hover:text-green-200 hover:translate-x-1 flex items-center gap-2"><Search className="w-4 h-4" /> Search Products</Link></li>
              <li><Link to="/cart" className="text-green-100 no-underline transition-all duration-300 text-sm hover:text-green-200 hover:translate-x-1 flex items-center gap-2"><ShoppingCart className="w-4 h-4" /> Shopping Cart</Link></li>
              <li><Link to="/wishlist" className="text-green-100 no-underline transition-all duration-300 text-sm hover:text-green-200 hover:translate-x-1 flex items-center gap-2"><Heart className="w-4 h-4" /> Wishlist</Link></li>
              <li><Link to="/orders" className="text-green-100 no-underline transition-all duration-300 text-sm hover:text-green-200 hover:translate-x-1 flex items-center gap-2"><Package className="w-4 h-4" /> Order History</Link></li>
            </ul>
          </div>

          {/* Support Links */}
          <div className="flex flex-col gap-3 sm:gap-4">
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-green-200 flex items-center gap-2">
              <Info className="w-5 h-5" /> Support
            </h4>
            <ul className="list-none m-0 p-0 flex flex-col gap-2 sm:gap-3">
              <li><Link to="/about" className="text-green-100 no-underline transition-all duration-300 text-sm hover:text-green-200 hover:translate-x-1 flex items-center gap-2"><Info className="w-4 h-4" /> About Us</Link></li>
              <li><Link to="/contact" className="text-green-100 no-underline transition-all duration-300 text-sm hover:text-green-200 hover:translate-x-1 flex items-center gap-2"><Phone className="w-4 h-4" /> Contact Us</Link></li>
              <li><Link to="/terms" className="text-green-100 no-underline transition-all duration-300 text-sm hover:text-green-200 hover:translate-x-1 flex items-center gap-2"><FileText className="w-4 h-4" /> Terms of Service</Link></li>
              <li><Link to="/privacy" className="text-green-100 no-underline transition-all duration-300 text-sm hover:text-green-200 hover:translate-x-1 flex items-center gap-2"><Lock className="w-4 h-4" /> Privacy Policy</Link></li>
            </ul>
          </div>

          {/* Connect */}
          <div className="flex flex-col gap-3 sm:gap-4">
            <h4 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4 text-green-200 flex items-center gap-2">
              <Leaf className="w-5 h-5" /> Connect
            </h4>
            <div className="flex gap-2 sm:gap-3">
              <a href="https://www.facebook.com/share/1B35KXYKmR/" target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-700 to-emerald-700 rounded-xl flex items-center justify-center text-white hover:from-green-600 hover:to-emerald-600 transition-all duration-300 hover:scale-110 shadow-lg">
                <Facebook className="w-5 h-5" />
              </a>
              <a href="https://www.instagram.com/green_guest_bd?igsh=MWFxaW5nNHo5ZTNoeQ==" target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-700 to-emerald-700 rounded-xl flex items-center justify-center text-white hover:from-green-600 hover:to-emerald-600 transition-all duration-300 hover:scale-110 shadow-lg">
                <Instagram className="w-5 h-5" />
              </a>
              <a href="https://youtube.com/@greenguestbd?si=PkHGn0bLNBZKi5FN" target="_blank" rel="noopener noreferrer" className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-green-700 to-emerald-700 rounded-xl flex items-center justify-center text-white hover:from-green-600 hover:to-emerald-600 transition-all duration-300 hover:scale-110 shadow-lg">
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>
              </a>
            </div>
          </div>
        </div>

        {/* Bottom Section */}
        <div className="border-t border-green-700 pt-6 sm:pt-8">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-green-100 text-sm text-center sm:text-left">
              © {year} {companyName}. All rights reserved.
            </p>
            <div className="flex items-center gap-3 sm:gap-4 flex-wrap justify-center">
              <Link to="/terms" className="text-green-100 no-underline text-sm transition-colors duration-300 hover:text-green-200">Terms of Service</Link>
              <span className="text-green-200 font-semibold">•</span>
              <Link to="/privacy" className="text-green-100 no-underline text-sm transition-colors duration-300 hover:text-green-200">Privacy Policy</Link>
              <span className="text-green-200 font-semibold">•</span>
              <Link to="/contact" className="text-green-100 no-underline text-sm transition-colors duration-300 hover:text-green-200">Contact Us</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer; 